/**
 * Damage and healing pipelines (PLAN §3, TC Unit.cpp):
 * avoidance/crit single roll → source mods → target mods/armor → block → absorbs → HP
 * → recent damage → threat → pushback → break-on-damage → resource rules → procs.
 */
import type { AuraInstanceId, DamageRequest, DamageResult, HealRequest, HealResult, HitType, SimContext, Unit } from '../contracts';
import { CRIT_MULTIPLIER } from '../contracts';
import { fireProcs, removeInstance } from './auras';
import { cheatDeathFloor, spendCheatDeath } from './cheatDeath'; // +mage-specs (S9)
import { transferDamage } from './absolution'; // +acolyte-specs (S6)
import { redirectShare } from './martyr'; // +acolyte-specs (S10)
import { companionSync } from './pets'; // +ranger-specs (S7 companions): Bond from synchronised hits
import { applyGainRules } from './resources';
import { controlState, critChanceFor, damageDoneMult, damageTakenMult, hasAuraFlag, healingDoneMult, leechFrac, unitEffectiveStats } from './stats';
import { addHealingThreat, addThreat } from './threat';
import { clamp, clamp01, emitClean, isHostile, RECENT_DAMAGE_KEEP_MS, unitOf } from './util';

export interface PipelineOpts {
  /** Override the source's crit chance (aura snapshots). */
  critChance?: number;
  depth?: number;
  /** Aura that produced this hit (its own breakOnDamage is not broken by it). */
  auraIid?: AuraInstanceId;
}

/**
 * One roll on the melee attack table: miss > dodge > parry > block > crit > hit.
 * No parry/block from behind (players also lose dodge from behind); casting or CC'd defenders cannot avoid at all.
 */
export function rollAttackTable(ctx: SimContext, attacker: Unit, defender: Unit, critChance: number, missBonus = 0): HitType {
  const a = unitEffectiveStats(ctx, attacker);
  const d = unitEffectiveStats(ctx, defender);
  const helpless = defender.cast !== null || !controlState(ctx, defender.id).canAct;
  const behind = ctx.sys.spatial.isBehind(ctx.world, attacker.id, defender.id);
  const roll = ctx.rng.next();
  let edge = clamp01(a.missChance + missBonus); // +autoattack: dual wield white swings miss more
  if (roll < edge) return 'miss';
  if (!helpless) {
    // TC RollMeleeOutcomeAgainst: players can't dodge attacks from behind (NPCs still can).
    if (!(behind && defender.kind === 'player') && roll < (edge += Math.max(0, d.dodge))) return 'dodge';
    if (!behind) {
      if (roll < (edge += Math.max(0, d.parry))) return 'parry';
      if (roll < (edge += Math.max(0, d.block))) return 'block';
    }
  }
  if (roll < (edge += Math.max(0, critChance))) return 'crit';
  return 'hit';
}

/** Parry-haste (TC): if > 20% of the swing remains, cut 40% of the swing, not below 20%. */
export function applyParryHaste(ctx: SimContext, defender: Unit): void {
  const sw = defender.swing;
  if (!sw) return;
  const speed = sw.speedMs / (1 + unitEffectiveStats(ctx, defender).haste);
  const remaining = sw.nextAt - ctx.now;
  if (remaining <= speed * 0.2) return;
  sw.nextAt = ctx.now + Math.round(Math.max(speed * 0.2, remaining - speed * 0.4));
}

const NO_DAMAGE: DamageResult = { hitType: 'miss', amount: 0, absorbed: 0, overkill: 0, killed: false };

/** Soak `amount` with absorb auras (application order). Emits `absorbed`. Returns total soaked. */
function consumeAbsorbs(ctx: SimContext, t: Unit, amount: number, req: DamageRequest): number {
  let left = amount;
  for (const inst of [...t.auras]) {
    if (left <= 0) break;
    const def = ctx.content.aura(inst.auraId);
    if (!def?.absorb || !inst.absorbRemaining || inst.absorbRemaining <= 0) continue;
    if (def.absorb.schools && !def.absorb.schools.includes(req.school)) continue;
    const take = Math.min(inst.absorbRemaining, left);
    inst.absorbRemaining -= take;
    left -= take;
    ctx.emit({ type: 'absorbed', sourceID: inst.casterId, targetID: t.id, abilityGameID: inst.auraId, attackerID: req.sourceId, extraAbilityGameID: req.abilityId, amount: take });
    if (inst.absorbRemaining <= 0) removeInstance(ctx, t, inst, 'consumed');
  }
  return amount - left;
}

export function dealDamage(ctx: SimContext, req: DamageRequest, opts: PipelineOpts = {}): DamageResult {
  const t = unitOf(ctx, req.targetId);
  if (!t?.alive || t.pet) return NO_DAMAGE; // +acolyte-specs (S7): pets never take damage
  if (req.selfHarm) return selfHarm(ctx, t, req, opts.depth ?? 0); // +specs
  const s = unitOf(ctx, req.sourceId);
  const depth = opts.depth ?? 0;
  const hostile = isHostile(s, t);
  const base = { type: 'damage' as const, sourceID: req.sourceId, targetID: req.targetId, abilityGameID: req.abilityId, school: req.school, isAoE: req.isAoE ?? false, tick: req.periodic ? true : undefined, stacks: req.stacks, ...(req.swing ? { swing: req.swing } : {}) };

  if (controlState(ctx, t.id).immuneDamage) {
    emitClean(ctx, { ...base, amount: 0, overkill: 0, absorbed: 0, unmitigatedAmount: Math.round(req.amount), hitType: 'immune' });
    if (hostile && s?.alive && t.ai?.mode !== 'evading') engage(ctx, s, t, 0);
    return { ...NO_DAMAGE, hitType: 'immune' };
  }

  let raw = req.amount;
  if (!req.preScaled && s) raw *= damageDoneMult(ctx, s, req.school, req.abilityId) * (1 + unitEffectiveStats(ctx, s).versatility) * s.scaling.damage;

  const canCrit = req.canCrit ?? true;
  const critChance = canCrit ? clamp01((opts.critChance ?? (s ? critChanceFor(ctx, s, req.abilityId) : 0)) + (req.critBonus ?? 0)) : 0;
  let hit: HitType;
  if (req.avoidable && s) hit = rollAttackTable(ctx, s, t, critChance, req.missBonus ?? 0);
  else hit = critChance > 0 && ctx.rng.chance(critChance) ? 'crit' : 'hit';

  if (hit === 'miss' || hit === 'dodge' || hit === 'parry') {
    emitClean(ctx, { ...base, amount: 0, overkill: 0, absorbed: 0, unmitigatedAmount: Math.round(raw), hitType: hit });
    if (hit === 'parry') applyParryHaste(ctx, t);
    if (hostile && s) engage(ctx, s, t, 0);
    // +lead (F6): the defender avoided an avoidable attack.
    fireProcs(ctx, t.id, 'avoided', s?.id ?? null, req.abilityId, depth, { hitType: hit });
    return { ...NO_DAMAGE, hitType: hit };
  }

  let amt = raw * (hit === 'crit' ? CRIT_MULTIPLIER : 1);
  const unmitigated = Math.round(amt);
  const teff = unitEffectiveStats(ctx, t);
  amt *= damageTakenMult(ctx, t, req.school);
  if (req.school === 'physical') amt *= 1 - clamp(teff.armor, 0, 0.85);
  amt *= 1 - clamp(teff.versatility / 2, 0, 1);
  let blocked = 0;
  if (hit === 'block') {
    blocked = Math.round(amt * clamp01(teff.blockAmount));
    amt -= blocked;
  }
  amt = Math.max(0, Math.round(amt));
  amt = redirectShare(ctx, t, amt, req, depth); // +acolyte-specs (S10): Martyr takes a share of allies' damage

  const absorbed = consumeAbsorbs(ctx, t, amt, req);
  const remaining = amt - absorbed;
  // +specs: Crimson Trance (cannotDie) leaves the holder at 1 HP at worst.
  // +mage-specs (S9): an unspent cheatDeath aura (Ashrise) turns a killing blow into 1 HP, once a fight.
  const saved = hasAuraFlag(ctx, t, 'cannotDie') ? null : cheatDeathFloor(ctx, t, remaining);
  const floor = saved || hasAuraFlag(ctx, t, 'cannotDie') ? 1 : 0;
  const dealt = Math.max(0, Math.min(t.hp - floor, remaining));
  const overkill = remaining - dealt;
  t.hp -= dealt;
  emitClean(ctx, { ...base, amount: dealt, overkill, absorbed, blocked: hit === 'block' ? blocked : undefined, unmitigatedAmount: unmitigated, hitType: hit });
  if (saved) spendCheatDeath(ctx, t, saved, depth);

  const taken = dealt + absorbed;
  if (taken > 0) {
    t.recentDamage.push({ at: ctx.now, amount: taken });
    const cutoff = ctx.now - RECENT_DAMAGE_KEEP_MS;
    while (t.recentDamage.length > 0 && t.recentDamage[0]!.at <= cutoff) t.recentDamage.shift();
  }
  if (hostile && s) {
    engage(ctx, s, t, taken * (req.threatMult ?? 1));
    // TC Unit::DealDamage: DoT damage delays channels only, never generic (hard) casts.
    if (dealt > 0 && t.cast && !(req.periodic && t.cast.kind === 'cast')) ctx.sys.casting.pushback(ctx, t.id);
    if (taken > 0) breakOnDamage(ctx, t, opts.auraIid);
  }
  if (taken > 0) {
    applyGainRules(ctx, t.id, 'damageTaken', req.abilityId, taken);
    if (s) applyGainRules(ctx, s.id, 'damageDealt', req.abilityId, taken);
  }
  if (hit === 'crit' && s) applyGainRules(ctx, s.id, 'critDealt', req.abilityId, taken);

  // +specs: leech (leechPct auras) heals the attacker for a share of what it dealt to a hostile.
  if (s?.alive && hostile && taken > 0) {
    const leech = leechFrac(ctx, s);
    if (leech > 0) heal(ctx, { sourceId: s.id, targetId: s.id, abilityId: req.abilityId, amount: taken * leech, preScaled: true, canCrit: false }, { depth: depth + 1 });
    transferDamage(ctx, s, taken, req, depth); // +acolyte-specs (S6): Absolution heals the marked allies
  }
  if (s && taken > 0) companionSync(ctx, s, t, req, taken); // +ranger-specs (S7 companions)
  if (s) {
    fireProcs(ctx, s.id, 'damageDealt', t.id, req.abilityId, depth);
    if (hit === 'crit') fireProcs(ctx, s.id, 'critDealt', t.id, req.abilityId, depth);
  }
  fireProcs(ctx, t.id, 'damageTaken', s?.id ?? null, req.abilityId, depth);
  if (hit === 'block') fireProcs(ctx, t.id, 'avoided', s?.id ?? null, req.abilityId, depth, { hitType: hit }); // +lead (F6)
  return { hitType: hit, amount: dealt, absorbed, overkill, killed: t.hp <= 0 };
}

/**
 * +specs: deliberate self-harm (Bloodletting): the raw amount comes off HP directly (no crit, no
 * mitigation, no absorbs, no threat) and never kills. It still counts as damage taken: recent damage,
 * resource gain rules (Thirst) and damageTaken procs.
 */
function selfHarm(ctx: SimContext, t: Unit, req: DamageRequest, depth: number): DamageResult {
  const amt = Math.max(0, Math.round(req.amount));
  const dealt = Math.max(0, Math.min(t.hp - 1, amt));
  t.hp -= dealt;
  emitClean(ctx, { type: 'damage', sourceID: req.sourceId, targetID: t.id, abilityGameID: req.abilityId, school: req.school, isAoE: false, amount: dealt, overkill: 0, absorbed: 0, unmitigatedAmount: amt, hitType: 'hit' });
  if (dealt > 0) {
    t.recentDamage.push({ at: ctx.now, amount: dealt });
    applyGainRules(ctx, t.id, 'damageTaken', req.abilityId, dealt);
    fireProcs(ctx, t.id, 'damageTaken', t.id, req.abilityId, depth);
  }
  return { hitType: 'hit', amount: dealt, absorbed: 0, overkill: 0, killed: false };
}

/** Hostile contact: threat on NPC victims, AI notification, combat flags. */
function engage(ctx: SimContext, s: Unit, t: Unit, threat: number): void {
  s.inCombat = true;
  t.inCombat = true;
  if (t.threat) {
    addThreat(ctx, t.id, s.id, threat);
    ctx.sys.ai.onAttacked(ctx, t.id, s.id);
  }
}

function breakOnDamage(ctx: SimContext, t: Unit, exceptIid?: AuraInstanceId): void {
  for (const inst of [...t.auras]) {
    if (inst.iid === exceptIid) continue;
    if (ctx.content.aura(inst.auraId)?.flags?.breakOnDamage) removeInstance(ctx, t, inst, 'cancelled');
  }
}

/** +encounters: heal-absorb auras (AuraDef.healAbsorb) eat incoming healing first. Returns the amount eaten. */
function consumeHealAbsorbs(ctx: SimContext, t: Unit, amount: number): number {
  let left = amount;
  for (const inst of [...t.auras]) {
    if (left <= 0) break;
    if (!inst.absorbRemaining || inst.absorbRemaining <= 0 || !ctx.content.aura(inst.auraId)?.healAbsorb) continue;
    const take = Math.min(inst.absorbRemaining, left);
    inst.absorbRemaining -= take;
    left -= take;
    if (inst.absorbRemaining <= 0) removeInstance(ctx, t, inst, 'consumed');
  }
  return amount - left;
}

export function heal(ctx: SimContext, req: HealRequest, opts: PipelineOpts = {}): HealResult {
  const t = unitOf(ctx, req.targetId);
  if (!t?.alive) return { hitType: 'hit', amount: 0, overheal: 0 };
  const s = unitOf(ctx, req.sourceId);
  let raw = req.amount;
  if (!req.preScaled && s) raw *= healingDoneMult(ctx, s, req.abilityId) * (1 + unitEffectiveStats(ctx, s).versatility);
  const canCrit = req.canCrit ?? true;
  const critChance = canCrit ? clamp01((opts.critChance ?? (s ? critChanceFor(ctx, s, req.abilityId) : 0)) + (req.critBonus ?? 0)) : 0;
  const hitType: 'hit' | 'crit' = critChance > 0 && ctx.rng.chance(critChance) ? 'crit' : 'hit';
  const gross = Math.max(0, Math.round(raw * (hitType === 'crit' ? CRIT_MULTIPLIER : 1) * unitEffectiveStats(ctx, t).healingTakenMult));
  const healAbsorbed = consumeHealAbsorbs(ctx, t, gross); // +encounters: heal absorbs eat healing first
  const amt = gross - healAbsorbed;
  const effective = Math.min(Math.max(0, t.maxHp - t.hp), amt);
  const overheal = amt - effective;
  t.hp += effective;
  emitClean(ctx, { type: 'heal', sourceID: req.sourceId, targetID: req.targetId, abilityGameID: req.abilityId, amount: effective, overheal, hitType, tick: req.periodic ? true : undefined, stacks: req.stacks, absorbed: healAbsorbed > 0 ? healAbsorbed : undefined });
  if (s && effective > 0) addHealingThreat(ctx, s.id, t.id, effective);
  if (s) {
    fireProcs(ctx, s.id, 'healDealt', t.id, req.abilityId, opts.depth ?? 0);
    if (hitType === 'crit') fireProcs(ctx, s.id, 'critHealed', t.id, req.abilityId, opts.depth ?? 0); // +lead (F14)
  }
  return { hitType, amount: effective, overheal };
}
